import React, { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Maximize } from "lucide-react";

const AMBER = "var(--rs-amber)";
const LINE = "rgba(255,255,255,0.55)";
const DIM = "rgba(255,255,255,0.28)";

function Label({ x, y, children, anchor = "middle", fill = LINE }) {
  return (
    <text x={x} y={y} textAnchor={anchor} fontSize="9.5" fill={fill} fontFamily="ui-monospace, monospace">
      {children}
    </text>
  );
}

class DiagramErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div
          className="rounded-md flex items-center justify-center text-[12px] text-[var(--rs-text-3)]"
          style={{ background: "rgba(0,0,0,0.25)", border: "1px solid var(--rs-border)", height: 180 }}
          data-testid="ff-diagram-error"
        >
          Diagram unavailable on this device — specifications below are unaffected.
        </div>
      );
    }
    return this.props.children;
  }
}

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

const DiagramSvg = React.memo(function DiagramSvg({ f, metricFirst, simple }) {
  const [view, setView] = useState({ k: 1, x: 0, y: 0 });
  const pointers = useRef(new Map());
  const gesture = useRef(null);

  const R = 118; // px radius for flange OD
  const s = R / (f.odMm / 2);
  const cx = 200, cy = 168;
  const rRf = (f.rfMm / 2) * s;
  const rBc = (f.bcMm / 2) * s;
  const rBore = (f.boreMm / 2) * s;
  const rGrvOd = (f.grvOdMm / 2) * s;
  const rGrvId = f.grvIdMm != null ? (f.grvIdMm / 2) * s : rGrvOd - 6;
  const rHole = Math.max(3, (f.holeMm / 2) * s);
  const holes = Array.from({ length: f.nBolts }, (_, i) => {
    const a = (i / f.nBolts) * Math.PI * 2 - Math.PI / 2;
    return { x: cx + rBc * Math.cos(a), y: cy + rBc * Math.sin(a) };
  });

  // side section
  const sy = 340;
  const halfW = R;
  const thkPx = Math.max(14, Math.min(42, f.thkMm * s));
  const hubHalf = (f.hubMm / 2) * s;

  const d = (mm, dp = 2) => (metricFirst ? `${mm.toFixed(0)}mm` : `${(mm / 25.4).toFixed(dp)}"`);
  const clampK = (k) => Math.min(4, Math.max(0.6, k));
  const zoom = (dk) => setView((v) => ({ ...v, k: clampK(v.k + dk) }));

  const startGesture = () => {
    const pts = [...pointers.current.values()];
    gesture.current = {
      view: { ...view },
      pts,
      d0: pts.length === 2 ? dist(pts[0], pts[1]) : 0,
    };
  };
  const onDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    startGesture();
  };
  const onMove = (e) => {
    if (!pointers.current.has(e.pointerId) || !gesture.current) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    const g = gesture.current;
    if (pts.length >= 2 && g.d0 > 0) {
      const scale = dist(pts[0], pts[1]) / g.d0;
      setView((v) => ({ ...v, k: clampK(g.view.k * scale) }));
    } else if (pts.length === 1 && g.pts.length === 1) {
      setView({ ...g.view, x: g.view.x + (pts[0].x - g.pts[0].x), y: g.view.y + (pts[0].y - g.pts[0].y) });
    }
  };
  const onUp = (e) => {
    pointers.current.delete(e.pointerId);
    gesture.current = pointers.current.size > 0 ? null : null;
    if (pointers.current.size > 0) startGesture();
  };

  // transform around fixed origin (200, 210) — no CSS transform-origin (Safari-safe)
  const { k, x, y } = view;
  const tf = `translate(${x + 200 * (1 - k)} ${y + 210 * (1 - k)}) scale(${k})`;

  return (
    <div className="relative rounded-md overflow-hidden" style={{ background: "rgba(0,0,0,0.25)", border: "1px solid var(--rs-border)" }} data-testid="ff-diagram">
      <div className="absolute top-2 right-2 z-10 flex gap-1.5">
        <button type="button" data-testid="ff-zoom-in" onClick={() => zoom(0.4)} className="p-1.5 rounded bg-[var(--rs-surface)] border" style={{ borderColor: "var(--rs-border)" }} aria-label="Zoom in"><ZoomIn className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid="ff-zoom-out" onClick={() => zoom(-0.4)} className="p-1.5 rounded bg-[var(--rs-surface)] border" style={{ borderColor: "var(--rs-border)" }} aria-label="Zoom out"><ZoomOut className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid="ff-zoom-reset" onClick={() => setView({ k: 1, x: 0, y: 0 })} className="p-1.5 rounded bg-[var(--rs-surface)] border" style={{ borderColor: "var(--rs-border)" }} aria-label="Reset view"><Maximize className="w-3.5 h-3.5" /></button>
      </div>
      <svg
        viewBox={simple ? "0 0 400 320" : "0 0 400 420"}
        className="w-full touch-none select-none"
        style={{ cursor: "grab", maxHeight: simple ? 320 : 420 }}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onPointerLeave={onUp}
      >
        <g transform={tf}>
          {/* ---- Front view ---- */}
          <circle cx={cx} cy={cy} r={R} fill="rgba(255,255,255,0.03)" stroke={LINE} strokeWidth="1.5" />
          <circle cx={cx} cy={cy} r={rRf} fill="none" stroke={DIM} strokeWidth="1" strokeDasharray="4 3" />
          <circle cx={cx} cy={cy} r={rBc} fill="none" stroke={DIM} strokeWidth="1" strokeDasharray="2 4" />
          <circle cx={cx} cy={cy} r={rGrvOd} fill="none" stroke={AMBER} strokeWidth="1.2" />
          <circle cx={cx} cy={cy} r={rGrvId} fill="none" stroke={AMBER} strokeWidth="1.2" opacity="0.7" />
          <circle cx={cx} cy={cy} r={rBore} fill="rgba(0,0,0,0.5)" stroke={LINE} strokeWidth="1.2" />
          {holes.map((h, i) => (
            <circle key={i} cx={h.x} cy={h.y} r={rHole} fill="rgba(0,0,0,0.45)" stroke={LINE} strokeWidth="1" />
          ))}
          {/* OD dimension */}
          <line x1={cx - R} y1={cy - R - 14} x2={cx + R} y2={cy - R - 14} stroke={AMBER} strokeWidth="0.8" />
          <line x1={cx - R} y1={cy - R - 19} x2={cx - R} y2={cy - R - 9} stroke={AMBER} strokeWidth="0.8" />
          <line x1={cx + R} y1={cy - R - 19} x2={cx + R} y2={cy - R - 9} stroke={AMBER} strokeWidth="0.8" />
          <Label x={cx} y={cy - R - 20} fill={AMBER}>OD {d(f.odMm)}</Label>
          {/* Bore */}
          <Label x={cx} y={cy + 3} fill={LINE}>{d(f.boreMm)}</Label>
          <Label x={cx} y={cy - rBore - 4}>BORE</Label>
          {/* BC label */}
          <Label x={cx + rBc * 0.72 + 4} y={cy - rBc * 0.72 - 6} anchor="start">BC {d(f.bcMm)}</Label>
          {/* Groove */}
          <Label x={cx - rGrvOd * 0.72 - 4} y={cy + rGrvOd * 0.72 + 12} anchor="end" fill={AMBER}>
            GRV {d(f.grvOdMm)}
          </Label>
          {!simple && (
            <>
              {/* centerlines */}
              <line x1={cx - R - 10} y1={cy} x2={cx + R + 10} y2={cy} stroke={DIM} strokeWidth="0.6" strokeDasharray="8 3 2 3" />
              <line x1={cx} y1={cy - R - 10} x2={cx} y2={cy + R + 10} stroke={DIM} strokeWidth="0.6" strokeDasharray="8 3 2 3" />
              {/* Bolt hole */}
              <Label x={holes[0].x + rHole + 4} y={holes[0].y + 3} anchor="start">
                {f.nBolts}× Ø{d(f.holeMm, 3)}
              </Label>
              {/* RF */}
              <Label x={cx - rRf * 0.7 - 4} y={cy - rRf * 0.7 - 4} anchor="end">RF {d(f.rfMm)}</Label>

              {/* ---- Side section ---- */}
              <rect x={cx - halfW} y={sy} width={halfW * 2} height={thkPx} fill="rgba(255,255,255,0.05)" stroke={LINE} strokeWidth="1.2" />
              <rect x={cx - hubHalf} y={sy + thkPx} width={hubHalf * 2} height={12} fill="rgba(255,255,255,0.04)" stroke={DIM} strokeWidth="1" />
              <rect x={cx - rGrvOd} y={sy} width={rGrvOd - rGrvId} height={4} fill={AMBER} opacity="0.8" />
              <rect x={cx + rGrvId} y={sy} width={rGrvOd - rGrvId} height={4} fill={AMBER} opacity="0.8" />
              <line x1={cx + halfW + 10} y1={sy} x2={cx + halfW + 10} y2={sy + thkPx} stroke={AMBER} strokeWidth="0.8" />
              <Label x={cx + halfW + 14} y={sy + thkPx / 2 + 3} anchor="start" fill={AMBER}>T {d(f.thkMm)}</Label>
              <Label x={cx} y={sy + thkPx + 24} fill={LINE}>HUB {d(f.hubMm)}</Label>
              <Label x={cx - halfW} y={sy - 6} anchor="start">SECTION</Label>
            </>
          )}
        </g>
      </svg>
    </div>
  );
});

// Lazy wrapper: renders the SVG only once scrolled into view, memoized, in an error boundary.
export const FlangeDiagram = React.memo(function FlangeDiagram({ f, metricFirst }) {
  const holder = useRef(null);
  const [visible, setVisible] = useState(false);
  const simple = typeof window !== "undefined" && window.innerWidth < 480;

  useEffect(() => {
    if (visible) return undefined;
    const el = holder.current;
    if (!el || typeof IntersectionObserver === "undefined") { setVisible(true); return undefined; }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); } },
      { rootMargin: "120px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  return (
    <div ref={holder} data-testid="ff-diagram-holder">
      {visible ? (
        <DiagramErrorBoundary>
          <DiagramSvg f={f} metricFirst={metricFirst} simple={simple} />
        </DiagramErrorBoundary>
      ) : (
        <div
          className="rounded-md flex items-center justify-center text-[11px] text-[var(--rs-text-3)]"
          style={{ background: "rgba(0,0,0,0.25)", border: "1px solid var(--rs-border)", height: simple ? 200 : 300 }}
          data-testid="ff-diagram-placeholder"
        >
          Loading diagram…
        </div>
      )}
    </div>
  );
});
